// PlanCard Component
// Displays a billing plan with features, pricing, and action buttons

import React from 'react';
import { FiCheck, FiStar } from 'react-icons/fi';
import { Button } from '../../ui/Button/Button';
import { Badge } from '../../ui/Badge/Badge';
import type { AvailablePlan } from '../../../api/billing';
import styles from './PlanCard.module.css';

interface PlanCardProps {
  plan: AvailablePlan;
  onUpgrade?: (planType: string) => void;
  disabled?: boolean;
  billingInterval?: 'monthly' | 'yearly';
  isCurrent?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function PlanCard({
  plan,
  onUpgrade,
  disabled = false,
  billingInterval = 'monthly',
  isCurrent = false,
  isSelected = false,
  onSelect,
}: PlanCardProps) {
  // Backend returns prices in cents, convert to dollars
  const priceInCents = billingInterval === 'monthly' ? plan.price.monthly : plan.price.yearly;
  const priceInDollars = priceInCents / 100;

  // Get currency symbol
  const getCurrencySymbol = (currency: string) => {
    switch (currency?.toLowerCase()) {
      case 'usd': return '$';
      case 'eur': return '€';
      case 'czk': return 'Kč';
      default: return '$';
    }
  };

  const currencySymbol = getCurrencySymbol(plan.price.currency);
  const priceDisplay = priceInDollars === 0 ? 'Free' : `${currencySymbol}${priceInDollars.toFixed(0)}`;
  const interval = billingInterval === 'monthly' ? '/mo' : '/yr';
  const isRecommended = plan.type === 'pro' || plan.type === 'agency';

  const getPlanColor = (planType: string) => {
    switch (planType) {
      case 'free':
        return 'var(--color-slate-500)';
      case 'starter':
        return 'var(--color-info)';
      case 'pro':
        return 'var(--color-accent)';
      case 'agency':
        return 'var(--color-primary)';
      case 'team':
        return 'var(--color-accent)';
      case 'enterprise':
        return 'var(--color-primary)';
      default:
        return 'var(--color-slate-500)';
    }
  };

  const formatLimit = (limit: number | null | undefined) => {
    if (limit === null || limit === undefined) return 'Unlimited';
    return limit.toLocaleString();
  };

  const handleUpgrade = () => {
    if (onUpgrade && !disabled && !plan.is_current) {
      onUpgrade(plan.type);
    }
  };

  // ✅ FIX: Handle features as either array or object
  const getFeaturesList = (): string[] => {
    if (!plan.features) return [];

    // If features is already an array, return it
    if (Array.isArray(plan.features)) {
      return plan.features;
    }

    // If features is an object (like BillingPlanFeatures), convert to array
    if (typeof plan.features === 'object') {
      return Object.entries(plan.features)
        .filter(([_, value]) => value === true)
        .map(([key, _]) => {
          // Convert snake_case to Title Case
          return key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        });
    }

    return [];
  };

  const featuresList = getFeaturesList();

  return (
    <div
      className={`${styles.planCard} ${plan.is_current ? styles.current : ''} ${isSelected ? styles.selected : ''}`}
      style={{ '--plan-color': getPlanColor(plan.type) } as React.CSSProperties}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.planName}>{plan.name}</h3>
          {plan.is_current && (
            <Badge variant="success" size="sm">
              Current Plan
            </Badge>
          )}
          {plan.type === 'enterprise' && !plan.is_current && (
            <FiStar className={styles.starIcon} />
          )}
        </div>
        <div className={styles.priceRow}>
          <span className={styles.price}>{priceDisplay}</span>
          {priceInDollars > 0 && <span className={styles.interval}>{interval}</span>}
        </div>
      </div>

      {/* Description */}
      {plan.description && (
        <p className={styles.description}>{plan.description}</p>
      )}

      {/* Limits */}
      <div className={styles.limits}>
        <div className={styles.limitItem}>
          <span className={styles.limitLabel}>Credits:</span>
          <span className={styles.limitValue}>{formatLimit(plan.limits.credits_per_month)}/mo</span>
        </div>
      </div>

      {/* Features */}
      <div className={styles.features}>
        <h4 className={styles.featuresTitle}>Features</h4>
        <ul className={styles.featuresList}>
          {featuresList.map((feature, index) => (
            <li key={index} className={styles.featureItem}>
              <FiCheck className={styles.checkIcon} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Action Button */}
      <div className={styles.actions}>
        {(isCurrent || plan.is_current) ? (
          <Button variant="ghost" size="md" disabled className={styles.actionButton}>
            Current Plan
          </Button>
        ) : (
          <Button
            variant={isRecommended ? 'accent' : 'primary'}
            size="md"
            onClick={onSelect || handleUpgrade}
            disabled={disabled || (!onSelect && !onUpgrade)}
            className={styles.actionButton}
          >
            {isSelected ? 'Selected' : onSelect ? 'Select Plan' : (plan.type === 'enterprise' ? 'Contact Sales' : 'Upgrade')}
          </Button>
        )}
      </div>
    </div>
  );
}

